import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { safeResource } from '@nestech/utils';
import { firstValueFrom } from 'rxjs';
import { SigningService, SignResult } from '../signing';
import { SignSessionResponse } from '../signing/signicat.model';
import { LanguageService } from '../util/language.service';

export interface CampaignDto {
  id: string;
  startDate: string;
  endDate: string;
  participantData: ParticipantDataDto[];
  documentMappingId?: string;
  campaignData: CampaignData;
}

export interface Translation {
  languageCode: string;
  text: string;
}

export interface CampaignData {
  title?: Translation[];
  description?: Translation[];
  questions: CampaignQuestionDto[];
  participantsMetaData?: ParticipantMetaData;
}

export interface ParticipantMetaData {
  title?: Translation[];
  description?: Translation[];
  keys: ParticipantMetaDataKey[];
}

export interface ParticipantMetaDataKey {
  key: string;
  label: Translation[];
  isVisible: boolean;
}

export type CampaignQuestionType = 'Radio' | 'Checkbox' | 'Select';
export interface CampaignQuestionDto {
  questionId: string;
  name: string;
  type: CampaignQuestionType;
  label?: Translation[];
  description?: Translation[];
  answers: CampaignAnswerDto[];
}

export interface CampaignAnswerDto {
  label?: Translation[];
  value: UnitTypeValue;
  description?: Translation[];
  answerId: string;
  subQuestion?: CampaignQuestionDto;
}

export interface ParticipantDataDto {
  nameValuePairs: NameValuePair[];
}

export interface NameValuePair {
  name: string;
  displayName?: string;
  hidden?: boolean;
  value: UnitTypeValue;
}

export interface UnitTypeValue {
  value: string;
  unitType: string;
}

export interface Campaign extends Omit<CampaignDto, 'startDate' | 'endDate'> {
  description: string;
  title: string;
  startDate: Date;
  endDate: Date;
  questions: CampaignQuestion[];
  tableData: ParticipantData[];
}

export interface ParticipantData extends Omit<ParticipantDataDto, 'title' | 'description'> {
  title: string;
  description: string;
}

export interface CampaignQuestion extends Omit<CampaignQuestionDto, 'answers' | 'description' | 'label'> {
  description: string;
  label: string;
  answers: CampaignAnswer[];
}

export interface CampaignAnswer extends Omit<CampaignAnswerDto, 'subQuestion' | 'description' | 'label'> {
  description: string;
  label: string;
  subQuestion?: CampaignQuestionWithAnswer;
}

export interface CampaignQuestionWithAnswer extends CampaignQuestion {
  answer?: CampaignAnswerWithAnswer;
}

export interface CampaignAnswerWithAnswer extends CampaignAnswer {
  answer?: CampaignQuestionWithAnswer;
}

export interface SaveResponse {
  signResponse?: SignSessionResponse;
  status: 'success' | 'error';
}

export interface SignResponse {
  signSessionResponse: SignSessionResponse | null;
  answerDtos: AnswerDto[];
}

export interface AnswerDto {
  questionId: string;
  answer?: string;
  name: string;
  subQuestionWithAnswer?: AnswerDto;
}

const DataRulePrefix = '/Data/Campaign';

const mapCampaignQuestionWithAnswerToAnswerDto = (answer: CampaignQuestionWithAnswer): AnswerDto => {
  const subQuestionAnswer = answer.answer?.subQuestion
    ? mapCampaignQuestionWithAnswerToAnswerDto(answer.answer?.subQuestion)
    : undefined;
  return {
    questionId: answer?.questionId,
    answer: answer.answer?.value.value,
    name: answer.name,
    subQuestionWithAnswer: subQuestionAnswer,
  };
};

const mapAnswerDtoToDataRule = (
  a: AnswerDto,
  answerIndex: number,
  questionIndex: number,
  prefix = `${DataRulePrefix}/Answers`,
): DataRule[] => {
  const currentPath = `${prefix}/choice-${questionIndex}/answer-${answerIndex}`;
  const subQuestionDataRule = a.subQuestionWithAnswer
    ? mapAnswerDtoToDataRule(a.subQuestionWithAnswer, 0, 0, currentPath)
    : undefined;
  return [
    {
      dataPath: currentPath,
      value: a.answer ?? '',
    },
  ].concat(subQuestionDataRule ?? []);
};

const getTranslation = (translations: Translation[] | undefined, languageCode: string): string => {
  return translations?.find(t => t.languageCode === languageCode)?.text ?? '';
};

const mapCampaignAnswerDtoToCampaignAnswer = (value: CampaignAnswerDto, selectedLanguage: string): CampaignAnswer => {
  return {
    ...value,
    description: getTranslation(value.description, selectedLanguage),
    label: getTranslation(value.label, selectedLanguage),
    subQuestion: value.subQuestion
      ? mapCampaignQuestionDtoToCampaignQuestion(value.subQuestion, selectedLanguage)
      : undefined,
  };
};

const mapCampaignQuestionDtoToCampaignQuestion = (
  question: CampaignQuestionDto,
  selectedLanguage: string,
): CampaignQuestion => {
  return {
    ...question,
    description: getTranslation(question.description, selectedLanguage),
    label: getTranslation(question.label, selectedLanguage),
    answers: question.answers.map(cv => mapCampaignAnswerDtoToCampaignAnswer(cv, selectedLanguage)),
  };
};

interface DataRule {
  dataPath: string;
  value: string;
}

const LanguageCodeToCampaignLanguage: Record<string, string> = {
  da: 'Da',
  en: 'En',
  fo: 'Fo',
};

@Injectable()
export class CampaignApi {
  #http = inject(HttpClient);
  #signingService = inject(SigningService);
  #language = inject(LanguageService).language;

  campaigns = safeResource({
    params: () => this.#language(),
    loader: ({ params: language }) => this.getCampaigns(language),
    defaultValue: [],
  });

  async getCampaigns(language: string, campaignId?: string): Promise<Campaign[]> {
    try {
      const selectedLanguage = LanguageCodeToCampaignLanguage[language] || 'Da';
      const campaigns = await firstValueFrom(
        this.#http.get<CampaignDto[]>(`/api/member/campaigns${campaignId ? `?campaignId=${campaignId}` : ''}`),
      );
      const result = campaigns.map(c => {
        const participantMetaData = c.campaignData.participantsMetaData;
        const keysToShow = participantMetaData?.keys?.filter(k => k.isVisible);
        const keysToShowAsList = keysToShow?.map(k => k.key);

        const tableData = c.participantData.map(pd => ({
          title: getTranslation(participantMetaData?.title, selectedLanguage),
          description: getTranslation(participantMetaData?.description, selectedLanguage),
          nameValuePairs: keysToShowAsList
            ? pd.nameValuePairs
                .filter(nv => keysToShowAsList.includes(nv.name))
                .map(nv => {
                  const keyMetaData = keysToShow?.find(k => k.key === nv.name);
                  return {
                    ...nv,
                    displayName: getTranslation(keyMetaData?.label, selectedLanguage) || nv.name,
                  };
                })
            : pd.nameValuePairs,
        }));

        return {
          ...c,
          startDate: new Date(Date.parse(c.startDate)),
          endDate: new Date(Date.parse(c.endDate)),
          tableData,
          description: getTranslation(c.campaignData.description, selectedLanguage),
          title: getTranslation(c.campaignData.title, selectedLanguage),
          questions: c.campaignData.questions.map(q => mapCampaignQuestionDtoToCampaignQuestion(q, selectedLanguage)),
        };
      });
      console.info(`[CampaignService] getCampaigns succeeded`, { count: result.length });
      return result;
    } catch (error) {
      console.error(`[CampaignService] getCampaigns failed`, { error });
      throw error;
    }
  }

  async signCampaignResponse(
    campaign: Campaign,
    answers: CampaignQuestionWithAnswer[],
    signTitle: string,
  ): Promise<SignResponse> {
    console.info(`[CampaignService] signCampaignResponse called`, { campaignId: campaign.id, signTitle });
    try {
      const answerDtos = answers.map(mapCampaignQuestionWithAnswerToAnswerDto);
      const answerDtosToDataRules = answerDtos.flatMap<DataRule>((a, i) => mapAnswerDtoToDataRule(a, i, 0));

      const xpathRegex = /[^a-zA-Z0-9]/g;
      const dataRuleSettings = campaign.participantData
        .flatMap<DataRule>((pd, index) =>
          pd.nameValuePairs.map(nv => ({
            dataPath: `${DataRulePrefix}/ParticipantData-${index}/${nv.name.replace(xpathRegex, '')}`,
            value: nv.value.value,
          })),
        )
        .concat(answerDtosToDataRules);

      if (campaign.documentMappingId) {
        const signSessionResponse = await this.#signingService.signGenericDocument({
          title: signTitle,
          fileName: 'kampagne-svar.pdf',
          redirectSettings: this.#signingService.redirectSettings(),
          documentMappingId: campaign.documentMappingId,
          dataRuleSettings: [
            {
              dataPath: `/Data/CampaignId`,
              value: campaign.id,
            },
          ].concat(dataRuleSettings),
        });
        console.info(`[CampaignService] signCampaignResponse succeeded`, { campaignId: campaign.id });
        return {
          signSessionResponse,
          answerDtos,
        };
      }
      console.info(`[CampaignService] signCampaignResponse: no documentMappingId`, { campaignId: campaign.id });
      return {
        signSessionResponse: null,
        answerDtos,
      };
    } catch (error) {
      console.error(`[CampaignService] signCampaignResponse failed`, { error });
      throw error;
    }
  }

  // call signCampaignResponse to get a sign response before calling this method
  async saveCampaign(campaign: Campaign, signResponse: SignResponse, signResult?: SignResult): Promise<SaveResponse> {
    console.info(`[CampaignService] saveCampaign called`, { campaignId: campaign.id });
    try {
      await firstValueFrom(
        this.#http.post(`/api/member/campaigns/${campaign.id}/answer`, {
          signicatResult: signResult,
          answers: signResponse.answerDtos,
        }),
      );
      this.campaigns.update(current => current?.filter(c => c.id !== campaign.id) ?? []);
      this.campaigns.reload();
      console.info(`[CampaignService] saveCampaign succeeded`, { campaignId: campaign.id });
      return { status: 'success' };
    } catch (error) {
      console.error(`[CampaignService] saveCampaign failed`, { error });
      throw error;
    }
  }
}
